import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import {
  ShieldCheck,
  ArrowUpRight,
  Lightbulb,
  Users,
  Rocket,
} from "lucide-react";
import { Reveal } from "../lib/motion";
import { useLang } from "../lib/i18n";
import { CtaButton, Kicker, Watermark } from "./Shared";
import { CywizMark, CywizText } from "./CywizMark";

const PRODUCT_IMG = "/images/deja-pret.webp";
const BRIDGE_IMG = "/images/pour-vous.webp";

const PROOF_ICONS = [Lightbulb, Users, Rocket];

/**
 * Bloc 4 — Cywiz : preuve concrète + pont vers le projet du prospect.
 */
export const Cywiz = () => {
  const { t } = useLang();
  const c = t.cywiz;
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [28, -28]);

  return (
    <section
      id="cywiz"
      data-testid="cywiz-section"
      ref={ref}
      className="relative pt-40 md:pt-56 pb-24 md:pb-36 overflow-hidden bg-canvas"
    >
      <Watermark n="03" />

      <div className="relative z-10 max-w-site mx-auto px-6 md:px-12">
        {/* ---- Preuve : texte + visuel ---- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <Reveal>
            <Kicker>{c.kicker}</Kicker>

            <h2 className="font-display text-4xl md:text-5xl uppercase text-cream leading-none">
              <CywizMark />
            </h2>

            <p className="mt-5 text-sm md:text-base uppercase tracking-[0.1em] text-warm font-light leading-snug max-w-lg">
              {c.tagline}
            </p>

            <p className="mt-7 max-w-lg text-base font-light text-warm leading-relaxed">
              <CywizText>{c.lead}</CywizText>
            </p>

            <div className="mt-7 max-w-lg rounded-xl border border-copper-light/50 bg-section/35 px-4 py-3.5 flex items-start gap-3">
              <ShieldCheck
                size={18}
                strokeWidth={1.6}
                className="shrink-0 mt-0.5 text-copper"
              />
              <p className="text-sm text-warm font-light leading-relaxed">
                {c.validation}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            {/* Pas de cadre : l’image flotte sur le fond de section */}
            <motion.div style={{ y: yImg }} className="relative -mx-2 lg:mx-0">
              <img
                src={PRODUCT_IMG}
                alt={t.common.cywizProductAlt}
                loading="lazy"
                decoding="async"
                className="cywiz-product-img w-full h-auto object-contain"
                draggable={false}
              />
            </motion.div>
          </Reveal>
        </div>

        {/* ---- Preuves terrain ---- */}
        <Reveal className="mt-14 md:mt-16" delay={0.06}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-0">
            {c.proofs.map((proof, i) => {
              const Icon = PROOF_ICONS[i];
              return (
                <div
                  key={proof.title}
                  className={`flex flex-col items-center text-center px-4 sm:px-6 ${
                    i > 0 ? "sm:border-l sm:border-copper-light/35" : ""
                  }`}
                >
                  <span className="inline-flex items-center justify-center w-16 h-16 md:w-[4.5rem] md:h-[4.5rem] rounded-full border border-copper/60 text-copper">
                    <Icon size={30} strokeWidth={1.35} aria-hidden />
                  </span>
                  <p className="mt-4 text-xs uppercase tracking-[0.22em] text-cream">
                    {proof.title}
                  </p>
                  <p className="mt-2 text-sm text-dim font-light leading-relaxed max-w-[16rem]">
                    {proof.text}
                  </p>
                </div>
              );
            })}
          </div>
        </Reveal>

        {/* ---- Pont vers le prospect ---- */}
        <Reveal className="mt-14 md:mt-16">
          <div
            data-testid="cywiz-bridge"
            className="rounded-2xl border border-copper-light/50 bg-section/50 px-5 py-5 sm:px-6 sm:py-6 md:pl-8 md:pr-5 md:py-6 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-5 md:gap-x-8 md:gap-y-5 lg:gap-x-10 items-center"
          >
            <img
              src={BRIDGE_IMG}
              alt=""
              aria-hidden
              loading="lazy"
              decoding="async"
              className="order-2 md:order-1 w-24 sm:w-28 md:w-40 lg:w-48 xl:w-52 h-auto object-contain shrink-0 mx-auto md:mx-0 md:mr-1 lg:mr-2 md:-translate-y-1"
              draggable={false}
            />

            <div className="order-1 md:order-2 min-w-0 text-center md:text-left overflow-x-auto">
              <Kicker variant="inline" className="text-xs tracking-[0.35em]">
                {c.bridge.kicker}
              </Kicker>
              <h3 className="mt-3 font-display font-normal leading-tight space-y-1">
                <span className="block text-cream text-xl sm:text-2xl md:text-[1.65rem] lg:text-3xl whitespace-nowrap">
                  <CywizText>{c.bridge.title[0]}</CywizText>
                </span>
                <span className="block text-gradient text-xl sm:text-2xl md:text-[1.65rem] lg:text-3xl whitespace-nowrap">
                  {c.bridge.title[1]}
                </span>
              </h3>
              {c.bridge.text && (
                <p className="mt-3 max-w-md text-sm text-warm font-light leading-relaxed mx-auto md:mx-0 whitespace-pre-line">
                  {c.bridge.text}
                </p>
              )}
            </div>

            <CtaButton
              variant="ghost"
              data-testid="cywiz-bridge-cta"
              href="#contact"
              className="group order-3 w-full md:w-auto self-center md:-translate-x-8 lg:-translate-x-10"
            >
              {c.bridge.cta}
              <ArrowUpRight
                size={14}
                strokeWidth={1.75}
                aria-hidden
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"
              />
            </CtaButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
